import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { creatorSpaceApi } from '@/lib/api/creatorSpace';
import { AIJobStatusIndicator } from '@/components/admin/ai-generation';
import { cn } from '@/lib/utils';
import { VStack, HStack, Grid } from '@/components/core';
import {
  LayoutDashboard,
  Users,
  User,
  Settings,
  DollarSign,
  Trophy,
  Gift,
  Heart,
  BarChart3,
  Shield,
  FileText,
  MapPin,
  Calendar,
  Bot,
  Coins,
  TrendingUp,
  Activity,
  Eye,
  Languages,
  X,
  Star,
  Bell,
  Mail,
  Crown,
  Zap,
  Brain,
  ChevronRight,
  Bug,
  CreditCard,
  Palette
} from 'lucide-react';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<any>;
  badge?: string | number;
  children?: NavItem[];
  isNested?: boolean;
  id?: string; // unique key for non-link groups
}

// Navigation items structure will be created dynamically (nutzerzentriert)
const createNavigationItems = (t: (key: string) => string, hasCreators = false): NavItem[] => [
  {
    title: t('navigation.dashboard'),
    href: '/admin/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: t('navigation.quickAccess'),
    href: '#',
    id: 'group-quick-access',
    icon: Zap,
    children: [
      { title: t('navigation.moderationCheck'), href: '/admin/moderation', icon: Shield },
      { title: t('navigation.characters'), href: '/admin/characters', icon: User },
      { title: t('navigation.users'), href: '/admin/users', icon: Users },
      { title: t('navigation.analytics'), href: '/admin/analytics', icon: BarChart3 },
    ]
  },
  {
    title: t('navigation.characters'),
    href: '#',
    id: 'group-characters',
    icon: User,
    children: [
      { title: t('navigation.overview'), href: '/admin/characters', icon: User },
      { title: t('navigation.creatorSpace'), href: '/admin/creator-space', icon: Crown },
      { title: t('navigation.locations'), href: '/admin/character-locations', icon: MapPin },
      { title: t('navigation.characterSettings'), href: '/admin/character-settings', icon: Settings },
    ]
  },
  {
    title: t('navigation.content'),
    href: '#',
    id: 'group-content',
    icon: Activity,
    children: [
      { title: t('navigation.users'), href: '/admin/users', icon: Users },
      { title: t('navigation.support'), href: '/admin/support', icon: Mail },
      { title: t('navigation.moderation'), href: '/admin/moderation', icon: Shield },
      { title: t('navigation.chatsReadOnly'), href: '/admin/user-chats', icon: Mail },
      { title: t('navigation.memories'), href: '/admin/memories', icon: Brain },
      { title: t('navigation.swipeSettings'), href: '/admin/swipe/settings', icon: Heart },
      { title: t('navigation.dailyChallenges'), href: '/admin/daily-challenges', icon: Calendar },
      { title: t('navigation.achievements'), href: '/admin/achievements', icon: Trophy },
    ]
  },
  {
    title: t('navigation.monetization'),
    href: '#',
    id: 'group-monetization',
    icon: DollarSign,
    children: [
      { title: t('navigation.platformBilling', 'Plattform-Abrechnung'), href: '/admin/partner-billing', icon: Coins },
      { title: t('navigation.billing', 'Billing & Preise'), href: '/admin/billing', icon: CreditCard },
      { title: t('navigation.paymentProviders', 'Zahlungsanbieter'), href: '/admin/payment-providers', icon: CreditCard },
      { title: t('navigation.userSubscriptions', 'Nutzer-Abos'), href: '/admin/user-subscriptions', icon: Users },
      { title: t('navigation.gifts'), href: '/admin/gifts', icon: Gift },
      { title: t('navigation.affiliate'), href: '/admin/affiliates', icon: TrendingUp },
    ]
  },
  {
    title: t('navigation.analytics'),
    href: '#',
    id: 'group-analytics',
    icon: BarChart3,
    children: [
      { title: t('navigation.dashboard'), href: '/admin/analytics', icon: BarChart3 },
      { title: t('navigation.revenue'), href: '/admin/analytics/revenue', icon: DollarSign },
      { title: t('navigation.swipeAnalytics'), href: '/admin/swipe/analytics', icon: Activity },
      { title: t('navigation.preferenceInsights'), href: '/admin/preference-insights', icon: Heart },
      { title: t('navigation.emailStatistics', 'E-Mail-Statistik'), href: '/admin/email-management', icon: Mail },
    ]
  },
  {
    title: t('navigation.communication'),
    href: '#',
    id: 'group-communication',
    icon: Bell,
    children: [
      { title: t('navigation.notificationSettings'), href: '/admin/notification-settings', icon: Settings },
      { title: t('navigation.emailConfiguration'), href: '/admin/email-settings', icon: Mail },
      { title: t('navigation.legal'), href: '/admin/legal', icon: FileText },
    ]
  },
  {
    title: t('navigation.system'),
    href: '#',
    id: 'group-system',
    icon: Settings,
    children: [
      { title: t('navigation.design'), href: '/admin/design', icon: Palette },
      { title: t('navigation.generalSettings'), href: '/admin/settings', icon: Settings },
      { title: t('navigation.languageSettings'), href: '/admin/language-settings', icon: Languages },
    ]
  },
  // Root-Admin Bereich (sichtbar nur für Root-Admin)
  {
    title: 'Root-Admin',
    href: '#',
    id: 'group-ssa',
    icon: Crown,
    children: [
      // System & Sicherheit
      { title: t('navigation.monitoring'), href: '/admin/monitoring', icon: Activity },
      { title: t('navigation.auditLogs'), href: '/admin/audit-logs', icon: Shield },
      // KI-Verwaltung
      { title: t('navigation.aiConfig'), href: '/admin/ai-settings', icon: Bot },
      { title: t('navigation.aiCosts'), href: '/admin/ai-costs', icon: DollarSign },
      { title: t('navigation.aiFallback'), href: '/admin/ai-fallback', icon: Activity },
      // Plattform & Daten
      { title: t('navigation.platformTariff', 'Tarif & Guthaben'), href: '/admin/platform-tariff', icon: DollarSign },
      { title: t('navigation.exportImport'), href: '/admin/data-transfer', icon: FileText },
      // Entwicklung
      { title: 'Debug', href: '/admin/debug', icon: Bug },
    ]
  },
];

const STORAGE_KEY = 'admin-sidebar-expanded';
const SCROLL_STORAGE_KEY = 'admin-sidebar-scroll';

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>(() => {
    // Load initial state from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const { user } = useAuth();
  const hasAutoExpandedRef = React.useRef(false);
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const hasRestoredScrollRef = React.useRef(false);

  // Restore scroll position after mount
  React.useEffect(() => {
    if (hasRestoredScrollRef.current) return;

    const restoreScroll = () => {
      try {
        const savedScroll = sessionStorage.getItem(SCROLL_STORAGE_KEY);
        if (savedScroll && scrollAreaRef.current) {
          const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
          if (viewport) {
            viewport.scrollTop = parseInt(savedScroll, 10);
            hasRestoredScrollRef.current = true;
          }
        }
      } catch {
        // Ignore errors
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(restoreScroll, 50);
    return () => clearTimeout(timer);
  }, []);

  // Save scroll position on scroll
  React.useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;

    const handleScroll = () => {
      try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(viewport.scrollTop));
      } catch {
        // Ignore errors
      }
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll while off-canvas is open on mobile (below md breakpoint)
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches; // Tailwind md breakpoint - 1
    if (isOpen && isMobile) {
      const body = document.body;
      const html = document.documentElement;
      const prevBodyOverflow = body.style.overflow;
      const prevHtmlOverflow = html.style.overflow;
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
      };
    }
    return;
  }, [isOpen]);

  // Check if any creators exist
  const { data: creators = [] } = useQuery({
    queryKey: ['creators', 'admin-count'],
    queryFn: () => creatorSpaceApi.getCreators(0, 1), // Only fetch 1 to check if any exist
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const hasCreators = creators.length > 0;

  // Create navigation items and filter sensitive sections for root_admin only (rekursiv)
  const navigationItems = React.useMemo(() => {
    let items = createNavigationItems(t, hasCreators);
    const isSSA = user?.role === 'root_admin';

    // On mobile, add app navigation links at the bottom
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      items = [
        ...items,
        {
          title: t('navigation.app'),
          href: '#',
          id: 'group-app-mobile',
          icon: Heart,
          children: [
            { title: t('navigation.characters'), href: '/characters', icon: Users },
            { title: t('navigation.chats'), href: '/chats', icon: Mail },
            ...(hasCreators ? [{ title: t('navigation.content'), href: '/creator-space', icon: Crown }] : []),
            { title: t('navigation.swipe'), href: '/swipe', icon: Heart },
            { title: t('navigation.likes'), href: '/matches', icon: Zap },
          ]
        }
      ];
    }
    // Routen, die nur für SSA sichtbar sein sollen
    const restricted = new Set([
      '/admin/ai-settings',
      '/admin/ai-costs',
      '/admin/ai-fallback',
      '/admin/data-transfer',
      '/admin/partners',
      '/admin/pricing-presets',
      '/admin/platform-tariff',
      '/admin/debug'
    ]);

    const filterItems = (nodes: NavItem[]): NavItem[] => {
      const result: NavItem[] = [];
      for (const node of nodes) {
        if (!isSSA && restricted.has(node.href)) {
          continue;
        }
        let children: NavItem[] | undefined = undefined;
        if (node.children && node.children.length > 0) {
          children = filterItems(node.children);
        }
        // Drop empty groups
        if ((node.href === '#' || node.isNested) && children && children.length === 0) {
          continue;
        }
        result.push(children ? { ...node, children } : node);
      }
      return result;
    };

    // Für Nicht-SSA: gesamten SSA-Bereich entfernen
    const filtered = filterItems(items).filter(node => isSSA || node.id !== 'group-ssa');
    return filtered;
  }, [hasCreators, user?.role]);

  // Auto-expand current section only on first load if no stored state exists
  React.useEffect(() => {
    // Skip if we already auto-expanded or if user has saved preferences
    if (hasAutoExpandedRef.current) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      hasAutoExpandedRef.current = true;
      return;
    }

    const currentPath = location.pathname;
    const shouldExpand: string[] = [];

    const getItemKey = (node: NavItem) => node.id || node.href;

    const checkForActiveChild = (item: NavItem, parentKey?: string): boolean => {
      let foundActive = false;
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(child => checkForActiveChild(child, getItemKey(item)));
        if (hasActiveChild) {
          shouldExpand.push(getItemKey(item));
          if (parentKey) {
            shouldExpand.push(parentKey);
          }
          foundActive = true;
        }
      }
      // consider only real links (not '#') for active check
      if (item.href && item.href !== '#') {
        if (currentPath === item.href || currentPath.startsWith(item.href + '/')) {
          foundActive = true;
        }
      }
      return foundActive;
    };

    navigationItems.forEach(item => {
      checkForActiveChild(item);
    });

    if (shouldExpand.length > 0) {
      const newExpanded = Array.from(new Set(shouldExpand));
      setExpandedItems(newExpanded);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newExpanded));
    }

    hasAutoExpandedRef.current = true;
  }, [navigationItems]);

  const getItemKey = (node: NavItem) => node.id || node.href;

  const toggleExpanded = (key: string) => {
    setExpandedItems(prev => {
      const newExpanded = prev.includes(key)
        ? prev.filter(item => item !== key)
        : [...prev, key];

      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newExpanded));

      return newExpanded;
    });
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const renderNavItem = (item: NavItem, level = 0) => {
    const active = isActive(item.href);
    const hasChildren = item.children && item.children.length > 0;
    const key = getItemKey(item);
    const isExpanded = expandedItems.includes(key);

    // Calculate padding based on level - kompakter auf Mobile
    const paddingLeft = level === 0 ? 'pl-2 md:pl-3' : level === 1 ? 'pl-6 md:pl-8' : level === 2 ? 'pl-10 md:pl-12' : 'pl-14 md:pl-16';

    // Different styling for nested groups
    const buttonClass = item.isNested
      ? 'text-sm font-normal'
      : level === 0
        ? 'font-semibold text-sm md:text-base'
        : 'font-medium text-sm';

    return (
      <div key={key}>
        {hasChildren ? (
          <Button
            variant="ghost"
            onClick={() => toggleExpanded(key)}
            className={cn(
              "w-full justify-start h-auto py-token-sm px-token-sm md:p-token-sm",
              paddingLeft,
              active
                ? 'font-medium border-r-2'
                : 'design-text-secondary hover:design-text-primary hover:bg-white/5',
              buttonClass
            )}
            style={active ? {
              color: 'var(--color-nav-icon-active)',
              backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 15%, transparent)',
              borderRightColor: 'var(--color-nav-icon-active)'
            } : undefined}
          >
            {(!item.isNested || item.id === 'group-ai') && (
              <item.icon className="h-4 w-4 md:h-5 md:w-5 mr-token-sm md:mr-token-sm flex-shrink-0" />
            )}
            <span className="flex-1 text-left truncate">{item.title}</span>
            {item.badge && (
              <Badge variant="secondary" className="ml-token-xs md:ml-token-xs text-xs">
                {item.badge}
              </Badge>
            )}
            <ChevronRight className={cn(
              "h-4 w-4 md:h-5 md:w-5 ml-auto transition-transform duration-200 flex-shrink-0 opacity-60",
              isExpanded && "rotate-90 opacity-100"
            )} />
          </Button>
        ) : (
          <Link to={item.href} onClick={item.href !== '#' ? onClose : undefined}>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start h-auto py-token-sm px-token-sm md:p-token-sm",
                paddingLeft,
                active
                  ? 'font-medium border-r-2'
                  : 'design-text-secondary hover:design-text-primary hover:bg-white/5',
                buttonClass
              )}
              style={active ? {
                color: 'var(--color-nav-icon-active)',
                backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 15%, transparent)',
                borderRightColor: 'var(--color-nav-icon-active)'
              } : undefined}
              onClick={(e) => {
                if (item.href === '#') {
                  e.preventDefault();
                }
              }}
            >
              <item.icon className="h-4 w-4 md:h-5 md:w-5 mr-token-sm md:mr-token-sm flex-shrink-0" />
              <span className="flex-1 text-left truncate">{item.title}</span>
              {item.badge && (
                <Badge variant="secondary" className="ml-token-xs md:ml-token-xs text-xs">
                  {item.badge}
                </Badge>
              )}
            </Button>
          </Link>
        )}

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <VStack className={cn(
            "gap-token-xs mt-token-xs",
            !item.isNested && "md:gap-token-xs md:mt-token-xs"
          )}>
            {item.children!.map(child => renderNavItem(child, level + 1))}
          </VStack>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop - nur auf Mobile (unter md) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar
          Mobile (<768px): Off-Canvas Drawer von links, über isOpen gesteuert
          Desktop (>=768px): Sticky Sidebar, immer sichtbar
      */}
      <aside
        className={cn(
          // Base Styles with admin-sidebar-fixed class for CSS override
          "admin-sidebar-fixed w-80 md:w-72 lg:w-80 flex-shrink-0",
          // Mobile: Fixed fullscreen, slide in/out, z-40 for mobile overlay
          "fixed top-0 left-0 h-screen z-40",
          // Transition and transform only on mobile, explicitly NONE on desktop
          "transition-transform duration-300 ease-in-out md:transition-none",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "md:!transform-none",
          // Desktop: FIXED (not sticky!) below AdminHeader, full height (footer will overlay)
          "md:fixed md:top-36 md:bottom-0 md:left-0 md:h-auto md:z-30"
        )}
      >
        <VStack className="h-full design-card border-r border-white/10 rounded-none">
          {/* Mobile Header - nur auf Mobile sichtbar */}
          <HStack align="center" justify="between" className="p-token-md border-b border-white/10 md:hidden flex-shrink-0">
            <HStack gap="md" align="center">
              <Shield className="h-5 w-5 text-brand-primary" />
              <h2 className="text-lg font-semibold design-text-primary">{t('sidebar.adminMenu')}</h2>
            </HStack>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-9 w-9">
              <X className="h-5 w-5" />
            </Button>
          </HStack>

          {/* Navigation - scrollbar with preserved scroll position */}
          <ScrollArea ref={scrollAreaRef} className="flex-1 px-token-sm py-token-md md:px-token-sm md:py-token-lg">
            {/* AI Job Status Indicator */}
            <AIJobStatusIndicator className="mb-token-md mx-token-xs" />

            <VStack as="nav" gap="sm">
              {navigationItems.map(item => renderNavItem(item))}
            </VStack>
          </ScrollArea>

          {/* Mobile Footer - Safe Area Padding */}
          <div className="h-20 md:hidden flex-shrink-0" />
        </VStack>
      </aside>
    </>
  );
}